import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  WavesGameSession,
  type WavesSessionStatus,
} from '../../game/waves/wavesSession';
import {
  buildTutorialWavesChart,
  getWavesTutorialOverlays,
  type WavesTutorialOverlay,
} from '../../game/waves/tutorialWavesChart';
import { markTutorialSeen } from '../../game/tutorialFlag';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import '../play.css';
import './wavesTutorial.css';

const TUTORIAL_CHART = buildTutorialWavesChart();

function pickOverlay(
  overlays: WavesTutorialOverlay[],
  beat: number,
): WavesTutorialOverlay {
  for (let i = overlays.length - 1; i >= 0; i -= 1) {
    const o = overlays[i];
    if (beat >= o.fromBeat && beat < o.untilBeat) {
      return o;
    }
  }
  return overlays[0];
}

/**
 * Quick onboarding for Waves mode. Runs a hand-crafted teaching chart
 * through {@link WavesGameSession} so a brand-new player learns the
 * "hold-to-climb, release-to-dive" rhythm in a controlled environment
 * before being dropped into the osu-derived song catalogue.
 *
 * Mirrors the Guitar tutorial's beat-synced overlay pattern (each
 * lesson chunk advances when the player's beat crosses a threshold).
 */
export function WavesTutorialPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<WavesGameSession | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [status, setStatus] = useState<WavesSessionStatus>('idle');
  const isTouch = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: coarse)').matches,
    [],
  );
  const overlays = useMemo(
    () => getWavesTutorialOverlays(isTouch),
    [isTouch],
  );
  const [overlay, setOverlay] = useState<WavesTutorialOverlay>(overlays[0]);

  useEffect(() => {
    saveModePref('waves');
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (status === 'idle') {
      return undefined;
    }
    let raf = 0;
    const loop = () => {
      const session = sessionRef.current;
      if (session) {
        const beat = session.getCurrentBeat();
        if (Number.isFinite(beat)) {
          const next = pickOverlay(overlays, beat);
          setOverlay((cur) => (cur.fromBeat === next.fromBeat ? cur : next));
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [overlays, status]);

  const startTutorial = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }
    setNeedsTap(false);
    if (sessionRef.current) {
      await sessionRef.current.destroy();
      sessionRef.current = null;
    }
    const session = new WavesGameSession({
      chart: TUTORIAL_CHART,
      canvas: canvasRef.current,
      audioOffsetMs: 0,
      onStatusChange: (s) => setStatus(s),
      onComplete: () => {
        markTutorialSeen('waves');
      },
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (err) {
      console.error('Failed to start Waves tutorial:', err);
      setNeedsTap(true);
    }
  }, []);

  useEffect(() => {
    if (!needsTap) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === ' ' ||
        e.code === 'Space' ||
        e.key === 'Enter' ||
        e.code === 'KeyW' ||
        e.code === 'KeyJ' ||
        e.code === 'ArrowUp'
      ) {
        e.preventDefault();
        void startTutorial();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, startTutorial]);

  const skipTutorial = () => {
    markTutorialSeen('waves');
    navigate(modePath('waves'));
  };

  return (
    <div className="play-page tutorial-page" data-testid="waves-tutorial-page">
      <div className="play-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="play-canvas"
          data-testid="waves-tutorial-canvas"
          onClick={() => {
            if (needsTap) {
              void startTutorial();
            }
          }}
        />

        {needsTap && (
          <div className="play-overlay" data-testid="waves-tutorial-start-overlay">
            <div className="play-overlay-card">
              <h2>Welcome to Waves</h2>
              <WavesTutorialDemo />
              <p>
                {isTouch ? 'Touch and hold' : (
                  <>
                    Hold <kbd>Space</kbd> (or <kbd>W</kbd> / <kbd>J</kbd> /{' '}
                    <kbd>↑</kbd>)
                  </>
                )}{' '}
                to fly up and release to dive. Thread the corridor between
                spikes — one touch ends the run.
              </p>
              <p className="muted faint">
                Short tutorial &middot; headphones recommended.
              </p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => void startTutorial()}
                  data-testid="waves-tutorial-start"
                >
                  Start tutorial
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={skipTutorial}
                  data-testid="waves-tutorial-skip"
                >
                  Skip &mdash; take me to the songs
                </button>
              </div>
            </div>
          </div>
        )}

        {(status === 'completed' || status === 'failed') && (
          <div
            className="play-overlay"
            data-testid="waves-tutorial-complete-overlay"
          >
            <div className="play-overlay-card">
              <h2>{status === 'completed' ? "You're ready" : 'Try again?'}</h2>
              <p>
                {status === 'completed'
                  ? "Pick a Normal-tier track to start collecting stars — every chart has a 1-dot Normal you can warm up on."
                  : "Take another shot — Waves normal-tier corridors leave plenty of room to recover."}
              </p>
              <div className="play-overlay-actions">
                <Link
                  to={modePath('waves')}
                  className="btn btn-primary btn-large"
                >
                  Browse tracks
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setNeedsTap(true);
                    setStatus('idle');
                  }}
                >
                  Replay tutorial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {!needsTap && status !== 'completed' && status !== 'failed' && (
        <div
          className="tutorial-overlay"
          data-testid="waves-tutorial-overlay-text"
        >
          <div className="tutorial-overlay-card">
            <h3>{overlay.title}</h3>
            <p>{overlay.body}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Looped SVG demo shown above "Start tutorial". A small triangle
 * snakes through a corridor with a floor-spike and ceiling-spike,
 * tracing the same piecewise-linear path the live engine produces
 * when the player toggles their button. Animation lives in
 * `wavesTutorial.css` keyframes.
 */
function WavesTutorialDemo() {
  return (
    <svg
      viewBox="0 0 320 130"
      className="waves-tutorial-demo"
      role="img"
      aria-label="Demo: hold to fly up and release to dive, threading between floor and ceiling spikes"
      data-testid="waves-tutorial-demo"
    >
      <defs>
        <linearGradient id="wt-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#06122a" />
          <stop offset="1" stopColor="#0c1f3d" />
        </linearGradient>
        <linearGradient id="wt-player" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a6f1ff" />
          <stop offset="1" stopColor="#3bd8ff" />
        </linearGradient>
      </defs>
      <rect width="320" height="130" fill="url(#wt-bg)" />

      <path d="M0,12 L320,12" className="waves-tutorial-demo-ceiling" />
      <path d="M0,118 L320,118" className="waves-tutorial-demo-floor" />

      <polygon points="80,12 92,32 68,32" className="waves-tutorial-demo-spike" />
      <polygon
        points="180,118 192,98 168,98"
        className="waves-tutorial-demo-spike"
      />
      <polygon points="270,12 282,32 258,32" className="waves-tutorial-demo-spike" />

      <path
        d="M16,80 L72,38 L152,98 L224,42 L304,82"
        className="waves-tutorial-demo-trail"
      />

      <g className="waves-tutorial-demo-player">
        <polygon points="-7,-7 7,0 -7,7" fill="url(#wt-player)" stroke="#ffffff" strokeWidth="1.4" />
      </g>

      <text className="waves-tutorial-demo-key" x="160" y="125" textAnchor="middle">
        SPACE
      </text>
      <text className="waves-tutorial-demo-hit" x="220" y="68" textAnchor="middle">
        HOLD!
      </text>
    </svg>
  );
}
